import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ProgressBar } from 'primeng/progressbar';
import { MediaService } from '../../../core/services/media.service';

@Component({
  selector: 'app-file-upload',
  imports: [
    ProgressBar,
    CommonModule
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent {

  @Input() acceptedTypes: string = 'image/jpeg,image/png,image/jpg,video/mp4,video/webm,video/ogg';
  maxFileSize: number = 10 * 1024 * 1024; // 5MB default
  maxVideoSize: number = 30 * 1024 * 1024; // 30MB for videos
  maxVideoDuration: number = 300; // 5 minutes in seconds

  @Output() fileSelectedEmitter = new EventEmitter<any>();


  @Input() files: {
    file?: any,
    image?: string,
    fileSize?: string,
    fileName?: string,
    type?: string,
    progress: number,
    error?: string,
    status?: string,
    id?: number,
    order?: number,
    isUploaded: boolean,
    model_id?: number,
    model_type?: string,
  }[] = [];

  errorMessage: string = '';

  filesIds: any = [];
  formData = new FormData();

  @Input() media_category: string = "vendor"
  @Input() mediaCount: number = 5;
  @Input() isDisable: boolean = false;
  @Input() invalidTypeErrorMessage: string = "*The valid types are ('jpg, png, jpeg, mp4, webm, ogg')";

  @Output() isUploadingEmitter = new EventEmitter<boolean>(false);
  isUploading: boolean = false;
  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private mediaService: MediaService
  ) {

  }


  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.errorMessage = "";
    if (input.files) {
      const files = Array.from(input.files);
      for (let file of files.slice(0, this.mediaCount)) {
        const reader = new FileReader();
        let fileItem: any = {
          file,
          progress: 0,
          duration: undefined,
          type: this.isVideoFile(file) ? 'video' : 'image',
          id: file?.name + file?.size + "_" + this.files.length + 1,
          fileName: file?.name,
          fileSize: this.formatFileSize(file?.size),
          order: this.files.length + 1,
          isUploaded: false,
        };

        // Check video duration for video files
        if (this.isVideoFile(file)) {
          try {
            this.getVideoDuration(file).then(duration => {
              fileItem.duration = duration;
              if (this.validate(fileItem)) {
                reader.onload = () => {
                  fileItem.image = this.sanitizer.bypassSecurityTrustUrl(reader.result as string);
                };
                reader.readAsDataURL(file);
                this.files.push(fileItem);
                this.uploadFile(fileItem);

              }
            });
          } catch (error) {
            console.warn('Could not get video duration:', error);
          }
        } else {
          if (this.validate(fileItem)) {
            reader.onload = () => {
              fileItem.image = this.sanitizer.bypassSecurityTrustUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
            this.files.push(fileItem);
            this.uploadFile(fileItem);


          } else {
            this.errorMessage = fileItem.error;

          }
        }
      }
    }

  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  }

  isValidFileType(file: File): boolean {
    const acceptedTypes = this.acceptedTypes.split(',').map(type => type.trim());
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type.match(type.replace('*', '.*'));
    });
  }

  isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  uploadFile(fileItem: any, reUpload: boolean = false) {

    let fileIndex = this.files.findIndex((file: any) => file?.id == fileItem?.id);
    this.formData = new FormData();
    this.formData.append("media_category", this.media_category);
    this.formData.append(`media[0]`, fileItem?.file);
    this.formData.append(`order`, '1');

    if (reUpload) {
      this.files[fileIndex]['status'] = "reUploading";
    }
    this.mediaService.uploadMedia(this.formData).subscribe({
      next: (resp: any) => {
        this.filesIds[fileIndex] = { id: resp[0]?.id, model_id: resp[0]?.model_id, model_type: resp[0]?.model_type };
        this.files[fileIndex]['image'] = resp[0]?.original_url;
        this.files[fileIndex]['model_type'] = resp?.model_type;
        this.files[fileIndex]['error'] = ""

        const item = this.files.find(obj => obj.id === fileItem?.id);
        if (item) {
          item.id = resp[0]?.id;
        }
        let intervalId = setInterval(() => {
          this.files[fileIndex]['status'] = 'uploading';
          this.isUploadingEmitter.emit(true);
          this.isUploading = true;
          this.files[fileIndex]['status'] = "";

          if (fileItem.progress >= 100) {
            clearInterval(intervalId);
            this.isUploading = false;
            this.isUploadingEmitter.emit(false);
            this.files[fileIndex]['status'] = 'uploaded';
            this.files[fileIndex]['isUploaded'] = true;
            this.fileSelectedEmitter.emit(this.filesIds);
            return;
          }
          fileItem.progress += 20;

        }, 500);
      }, error: error => {
        this.files[fileIndex]['isUploaded'] = false;
        this.files[fileIndex]['status'] = "";
        this.isUploading = false;
        this.files[fileIndex]['error'] = "Upload failed"
      }
    })

  }

  validate(fileItem: any) {
    const file = fileItem.file;

    // Check file type
    if (!this.isValidFileType(file)) {
      fileItem.error = this.invalidTypeErrorMessage;
      this.errorMessage = this.invalidTypeErrorMessage;
    }

    // Check file size
    const maxSize = this.isVideoFile(file) ? this.maxVideoSize : this.maxFileSize;
    if (file.size >= maxSize) {
      fileItem.error = `*Invalid file size (max size 10MB)`;
      this.errorMessage = `*Invalid file size (max size 10MB)`;
    }

    if (this.isVideoFile(file) && fileItem?.duration && fileItem.duration > this.maxVideoDuration) {
      fileItem.error = `*Max video duration is ${this.maxVideoDuration / 60} minutes`;
      this.errorMessage = `*Max video duration is ${this.maxVideoDuration / 60} minutes`;
    }
    if (!fileItem.error) {
      return true;
    } else {
      return false;
    }
  }

  clearImg(fileItem: any) {
    const index = this.files.findIndex((file: any) => file?.id == fileItem?.id);
    if (this.files[index].isUploaded) {
      let body = {
        "id": fileItem?.id,
        "model_type": this.filesIds[index]?.model_type || this.files[index]?.model_type,
        "model_id": this.filesIds[index]?.model_id ? this.filesIds[index]?.model_id : this.files[index].model_id,
      }
      this.files[index].status = 'loadingDelete';
      this.mediaService.deleteMedia(body).subscribe({
        next: resp => {
          this.files[index].status = '';
          if (index > -1) {
            this.files.splice(index, 1);
            this.filesIds.splice(index, 1);
          }
          this.fileSelectedEmitter.emit(this.files);

        }, error: error => {
          this.files[index].status = '';

        }
      })

    } else {
      if (index > -1) {
        this.files.splice(index, 1);
        this.filesIds.splice(index, 1);
      }
      this.fileSelectedEmitter.emit(this.files);

    }

  }



}
